import { useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';

import { DEFAULT_PAGINATION_DATA } from 'lib/constants';
import {
    DarxApplicationDtOsAdsDtOsAdsGetAllDto,
    useGetApiV1ClientAdsQuery,
} from 'service/api/allApis';
import CustomSelectPrimary from 'components/common/custom-select/primary';
import AdvertCard from 'components/common/advert-card';
import AdvertWrapper from 'layout/advert-wrapper';
import { RenderIf } from 'components/common/render-if';
import { Spinner } from 'components/common/spinner';
import {
    ADVERT_LIST_SORT_TYPE,
    ADVERT_OWNER_TYPE,
    ADVERT_TYPE,
    BUILDING_TYPE,
    DOCUMENT_STATUS,
    MAINTENANCE_STATUS,
    MORTGAGE_STATUS,
    PRICE_TYPE,
    PROPERTY_TYPE,
} from 'types';
import { getArrayValue } from 'lib/utils';

function getFilterParams(searchParams: URLSearchParams) {
    const advertType = searchParams.get('advertType')
        ? (searchParams.get('advertType')! as ADVERT_TYPE)
        : undefined;
    const advertOwnerType = searchParams.get('advertOwnerType')
        ? (searchParams.get('advertOwnerType')! as ADVERT_OWNER_TYPE)
        : undefined;
    const propertyType = searchParams.get('propertyType')
        ? (searchParams.get('propertyType')! as PROPERTY_TYPE)
        : undefined;
    const cityIds = getArrayValue(searchParams.getAll('cityId'));
    const countryIds = getArrayValue(searchParams.getAll('countryId'));
    const districtIds = getArrayValue(searchParams.getAll('districtIds'));
    const metroIds = getArrayValue(searchParams.getAll('metroIds'));
    const targetIds = getArrayValue(searchParams.getAll('targetIds'));

    const maintenanceStatuses = getArrayValue(
        searchParams.getAll('maintenanceStatus').map((item) => item) as MAINTENANCE_STATUS[],
    );

    const priceType = searchParams.get('priceType')
        ? (searchParams.get('priceType')! as PRICE_TYPE)
        : undefined;
    const buildingType = searchParams.get('buildingType')
        ? (searchParams.get('buildingType')! as BUILDING_TYPE)
        : undefined;
    const documentStatus = searchParams.get('documentStatus')
        ? (searchParams.get('documentStatus')! as DOCUMENT_STATUS)
        : undefined;
    const mortgageStatus = searchParams.get('mortgageStatus')
        ? (searchParams.get('mortgageStatus')! as MORTGAGE_STATUS)
        : undefined;
    const maxPrice = searchParams.get('maxPrice') ? +searchParams.get('maxPrice')! : undefined;
    const minPrice = searchParams.get('minPrice') ? +searchParams.get('minPrice')! : undefined;
    const roomCount = searchParams.get('roomCount') ? +searchParams.get('roomCount')! : undefined;
    const maxPropertyArea = searchParams.get('maxPropertyArea')
        ? +searchParams.get('maxPropertyArea')!
        : undefined;
    const minPropertyArea = searchParams.get('minPropertyArea')
        ? +searchParams.get('minPropertyArea')!
        : undefined;
    const maxPropertyFloor = searchParams.get('maxPropertyFloor')
        ? +searchParams.get('maxPropertyFloor')!
        : undefined;
    const minPropertyFloor = searchParams.get('minPropertyFloor')
        ? +searchParams.get('minPropertyFloor')!
        : undefined;
    const isVip = searchParams.get('isVip') ? searchParams.get('isVip') === 'true' : undefined;
    const isPremium = searchParams.get('isPremium')
        ? searchParams.get('isPremium') === 'true'
        : undefined;
    let sortByPrimary;
    let sortDirectionPrimary;

    const sort = searchParams.get('sort');
    if (sort) {
        switch (+sort) {
            case ADVERT_LIST_SORT_TYPE.CHEAPEST:
                sortByPrimary = 'price';
                sortDirectionPrimary = 'asc';
                break;
            case ADVERT_LIST_SORT_TYPE.EXPENSIVE:
                sortByPrimary = 'price';
                sortDirectionPrimary = 'desc';
                break;
            case ADVERT_LIST_SORT_TYPE.NEWEST:
                sortByPrimary = 'createdAt';
                sortDirectionPrimary = 'desc';
                break;
            case ADVERT_LIST_SORT_TYPE.OLDEST:
                sortByPrimary = 'createdAt';
                sortDirectionPrimary = 'asc';
                break;
        }
    }

    return {
        advertType,
        advertOwnerType,
        propertyType,
        countryIds,
        cityIds,
        districtIds,
        targetIds,
        metroIds,
        priceType,
        buildingType,
        documentStatus,
        maxPrice,
        minPrice,
        roomCount,
        maintenanceStatuses,
        maxPropertyArea,
        minPropertyArea,
        maxPropertyFloor,
        minPropertyFloor,
        mortgageStatus,
        sortByPrimary,
        sortDirectionPrimary,
        isVip,
        isPremium,
    };
}

export const AdvertList = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION_DATA);
    const [items, setItems] = useState<DarxApplicationDtOsAdsDtOsAdsGetAllDto[]>([]);

    const { data, isLoading, isFetching, status } = useGetApiV1ClientAdsQuery(
        {
            ...pagination,
            ...getFilterParams(searchParams),
        },
        {
            refetchOnMountOrArgChange: true,
        },
    );
    const loading = isLoading || isFetching;

    function getNextPage() {
        setPagination({ ...pagination, pageNumber: pagination.pageNumber + 1 });
    }

    const hasMore = !!(
        data &&
        data.totalPages &&
        data.pageNumber &&
        data?.totalPages > data?.pageNumber
    );

    const sortOptions = [
        {
            value: String(ADVERT_LIST_SORT_TYPE.CHEAPEST),
            label: t('filter.order.cheapest'),
        },
        {
            value: String(ADVERT_LIST_SORT_TYPE.EXPENSIVE),
            label: t('filter.order.expensive'),
        },
        {
            value: String(ADVERT_LIST_SORT_TYPE.NEWEST),
            label: t('filter.order.newest'),
        },
        {
            value: String(ADVERT_LIST_SORT_TYPE.OLDEST),
            label: t('filter.order.oldest'),
        },
    ];

    useEffect(() => {
        if (status === 'fulfilled' && data) {
            const items = data?.items ?? [];
            setItems((prev) => (pagination.pageNumber > 1 ? [...prev, ...items] : items));
        }
    }, [status, data?.items]);

    return (
        <div className="md:mt-6 w-full px-5 md:px-0 md:w-[925px] lg:w-[1240px] mx-auto z-[1] relative">
            <div className="mb-4 flex justify-between items-center md:min-h-[52px]">
                <p className="text-base md:text-2xl font-bold text-gray-800 w-fit">
                    {t('advert.list-title')}{' '}
                    {data?.totalItems && (
                        <span className="text-white md:text-gray-500 font-semibold text-xs md:text-sm block">
                            {t('advert.list-count', { count: data?.totalItems })}
                        </span>
                    )}
                </p>
                <CustomSelectPrimary
                    options={sortOptions}
                    value={searchParams.get('sort')}
                    onChange={(value) => {
                        searchParams.set('sort', value);
                        setSearchParams(searchParams);
                    }}
                    placeholder={t('filter.sort')}
                    className="w-[200px]"
                />
            </div>
            <InfiniteScroll
                dataLength={items.length}
                next={getNextPage}
                hasMore={hasMore}
                loader={<Spinner size={40} color="rgb(177,146,76)" className="mt-6 mx-auto " />}
                className="overflow-visible"
            >
                <AdvertWrapper>
                    <RenderIf condition={isLoading}>
                        {[...Array(8)].map((_, index) => (
                            <AdvertCard.Skeleton key={index} />
                        ))}
                    </RenderIf>
                    <RenderIf condition={!isLoading}>
                        {items.map((advert) => (
                            <AdvertCard key={advert.id} advert={advert} />
                        ))}
                    </RenderIf>
                </AdvertWrapper>
            </InfiniteScroll>
            <RenderIf condition={!loading && !items.length}>
                <p className="w-full text-center pt-12 text-3xl text-muted-foreground">
                    {t('general.no-result')}
                </p>
            </RenderIf>
        </div>
    );
};
